import { Orientation } from '../hooks/useOrientationChange';

/**
 * Utility functions for handling device orientation and responsive layouts
 */

/**
 * Get the current device orientation based on window dimensions
 * 
 * @returns 'portrait' if height > width, 'landscape' otherwise
 */
export const getOrientation = (): Orientation => {
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
};

/**
 * Check if the device is currently in portrait mode
 * 
 * @returns true if in portrait mode
 */
export const isPortrait = (): boolean => {
    return getOrientation() === 'portrait';
};

/**
 * Check if the device is currently in landscape mode
 * 
 * @returns true if in landscape mode
 */
export const isLandscape = (): boolean => {
    return getOrientation() === 'landscape';
};

/**
 * Get window dimensions
 * 
 * @returns Object with width and height in pixels
 */
export const getWindowDimensions = () => {
    return {
        width: window.innerWidth,
        height: window.innerHeight,
    };
};

/**
 * Get the aspect ratio of the current window
 * 
 * @returns Aspect ratio (width / height)
 */
export const getAspectRatio = (): number => {
    return window.innerWidth / window.innerHeight;
};

/**
 * Common device breakpoints in pixels
 */
export const BREAKPOINTS = {
    /** Mobile phones (portrait) */
    MOBILE_PORTRAIT: 480,
    /** Mobile phones (landscape) and small tablets */
    MOBILE_LANDSCAPE: 768,
    /** Tablets (portrait) */
    TABLET_PORTRAIT: 768,
    /** Tablets (landscape) and small laptops */
    TABLET_LANDSCAPE: 1024,
    /** Laptops and desktops */
    DESKTOP: 1280,
    /** Large desktops */
    DESKTOP_LARGE: 1920,
} as const;

/**
 * iPad-specific breakpoints
 */
export const IPAD_BREAKPOINTS = {
    /** iPad Mini / iPad (portrait) */
    IPAD_PORTRAIT: 768,
    /** iPad Mini / iPad (landscape) */
    IPAD_LANDSCAPE: 1024,
    /** iPad Pro 11" (portrait) */
    IPAD_PRO_11_PORTRAIT: 834,
    /** iPad Pro 11" (landscape) */
    IPAD_PRO_11_LANDSCAPE: 1194,
    /** iPad Pro 12.9" (portrait) */
    IPAD_PRO_129_PORTRAIT: 1024,
    /** iPad Pro 12.9" (landscape) */
    IPAD_PRO_129_LANDSCAPE: 1366,
} as const;

/**
 * Device type detection
 */
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Detect device type based on screen width
 * 
 * @param width - Window width in pixels (defaults to current window width)
 * @returns Device type: 'mobile', 'tablet', or 'desktop'
 */
export const getDeviceType = (width: number = window.innerWidth): DeviceType => {
    if (width < BREAKPOINTS.MOBILE_LANDSCAPE) {
        return 'mobile';
    } else if (width < BREAKPOINTS.DESKTOP) {
        return 'tablet';
    } else {
        return 'desktop';
    }
};

/**
 * Check if the current device is likely an iPad
 * 
 * This checks both user agent and screen dimensions to detect iPad devices.
 * Note: iPadOS 13+ reports as Mac in user agent, so we also check dimensions.
 * 
 * @returns true if device is likely an iPad
 */
export const isIPad = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIPadUserAgent = /ipad/.test(userAgent);

    // iPadOS 13+ reports as Mac, so check for touch + Mac + specific dimensions
    const isMacWithTouch = /macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;

    // Check if dimensions match common iPad sizes
    const width = window.innerWidth;
    const height = window.innerHeight;
    const largerDimension = Math.max(width, height);
    const smallerDimension = Math.min(width, height);

    const isIPadDimensions = (
        (smallerDimension === 768 && largerDimension === 1024) || // iPad, iPad Mini
        (smallerDimension === 834 && largerDimension === 1194) || // iPad Pro 11"
        (smallerDimension === 1024 && largerDimension === 1366)   // iPad Pro 12.9"
    );

    return isIPadUserAgent || (isMacWithTouch && isIPadDimensions);
};

/**
 * Check if the current device is a mobile phone
 * 
 * @returns true if device is a mobile phone
 */
export const isMobilePhone = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|android.*mobile/.test(userAgent);
};

/**
 * Check if the current device is a tablet (including iPad)
 * 
 * @returns true if device is a tablet
 */
export const isTablet = (): boolean => {
    return isIPad() || /android(?!.*mobile)/.test(navigator.userAgent.toLowerCase());
};

/**
 * Get safe area insets for devices with notches/rounded corners
 * 
 * @returns Object with top, right, bottom, left insets in pixels
 */
export const getSafeAreaInsets = () => {
    const style = getComputedStyle(document.documentElement);
    const px = (name: string) => {
        const v = style.getPropertyValue(name).trim();
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
    };

    return {
        top: px('--safe-area-inset-top'),
        right: px('--safe-area-inset-right'),
        bottom: px('--safe-area-inset-bottom'),
        left: px('--safe-area-inset-left'),
    };
};

/**
 * Calculate responsive dimensions based on orientation
 * 
 * Useful for components that need different sizing in portrait vs landscape
 * 
 * @param portraitValue - Value to use in portrait mode
 * @param landscapeValue - Value to use in landscape mode
 * @returns The appropriate value based on current orientation
 * 
 * @example
 * ```typescript
 * const cardHeight = getResponsiveDimension('70vh', '60vh');
 * const cardWidth = getResponsiveDimension('85vw', '70vw');
 * ```
 */
export const getResponsiveDimension = <T>(portraitValue: T, landscapeValue: T): T => {
    return isPortrait() ? portraitValue : landscapeValue;
};

/**
 * Calculate optimal card dimensions for swipe interfaces
 * 
 * This is specifically designed for card-based UIs like MovieTasteStepV2
 * where cards need to fit properly in both orientations.
 * 
 * @param orientation - Current orientation
 * @param viewportWidth - Viewport width in pixels
 * @param viewportHeight - Viewport height in pixels
 * @returns Object with recommended height and width percentages
 */
export const getOptimalCardDimensions = (
    orientation: Orientation,
    viewportWidth: number,
    viewportHeight: number
) => {
    const isPortraitMode = orientation === 'portrait';

    return {
        // Height as percentage of viewport
        heightPercent: isPortraitMode ? 0.70 : 0.65, // 70% in portrait, 65% in landscape
        // Width as percentage of viewport
        widthPercent: isPortraitMode ? 0.85 : 0.50, // 85% in portrait, 50% in landscape
        // Calculated pixel values
        height: viewportHeight * (isPortraitMode ? 0.70 : 0.65),
        width: viewportWidth * (isPortraitMode ? 0.85 : 0.50),
    };
};

/**
 * Debounce function for event handlers
 * 
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout | null = null;

    return (...args: Parameters<T>) => {
        if (timeout) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
};

/**
 * Create a media query listener for orientation changes
 * 
 * @param callback - Function to call when orientation changes
 * @returns Cleanup function to remove the listener
 * 
 * @example
 * ```typescript
 * const cleanup = createOrientationListener(() => {
 *   console.log('Orientation changed!');
 * });
 * 
 * // Later, when component unmounts:
 * cleanup();
 * ```
 */
export const createOrientationListener = (callback: (orientation: Orientation) => void) => {
    const portraitQuery = window.matchMedia('(orientation: portrait)');
    const landscapeQuery = window.matchMedia('(orientation: landscape)');

    const handleChange = () => {
        callback(getOrientation());
    };

    // Modern browsers
    if (portraitQuery.addEventListener) {
        portraitQuery.addEventListener('change', handleChange);
        landscapeQuery.addEventListener('change', handleChange);

        return () => {
            portraitQuery.removeEventListener('change', handleChange);
            landscapeQuery.removeEventListener('change', handleChange);
        };
    }
    // Fallback for older browsers
    else {
        portraitQuery.addListener(handleChange);
        landscapeQuery.addListener(handleChange);

        return () => {
            portraitQuery.removeListener(handleChange);
            landscapeQuery.removeListener(handleChange);
        };
    }
};
